/*import {  SampleSummaryResult } from "./result.model";*/
/**
 * Metadata associated with a single file used in testing or calibration.
 */
export class FileMetaData {
  constructor(fileMetaData: FileMetaData) {
    this.runId = fileMetaData.runId;
    this.date = fileMetaData.date;
    this.instrumentSerialNo = fileMetaData.instrumentSerialNo;
    this.softwareVersion = fileMetaData.softwareVersion;
    this.fileName = fileMetaData.fileName;
    this.type = fileMetaData.type;
    this.accessions = [...(fileMetaData?.accessions ?? [])];
    this.reagentLots = [...(fileMetaData?.reagentLots ?? [])];
    this.panels = [...(fileMetaData?.panels ?? [])];
    this.instrumentId = fileMetaData?.instrumentId ?? '';
    this.fileValidity = fileMetaData?.fileValidity ?? null;
    this.replicateCounts = { ...(fileMetaData?.replicateCounts ?? {}) };
  }

  runId: string = '';
  date: string = '';
  instrumentSerialNo: string = '';
  softwareVersion: string = '';
  fileName: string = '';
  type: string = '';
  accessions: string[] = [];
  reagentLots: number[] = [];
  panels: PatientFilePanelInfo[] = [];
  instrumentId: string = '';
  fileValidity?: boolean | null = null;
  replicateCounts?: Record<string, number[]> = {};
}
/**
 * Represents panel information for a patient file.
 */
export class PatientFilePanelInfo {
  name: string = '';
  assays: PatientFileAssayInfo[] = [];
}
/**
 * Information about an individual assay in a panel.
 */
export class PatientFileAssayInfo {
  name: string = '';
  region: string = '';
}
/**
 * Response structure for multiple types of file metadata.
 */
export class FileMetaDataResponse {
  runData!: FileMetaData[];
  calibrationData!: FileMetaData[];
  controlData!: FileMetaData[];
}
/**
 * Response structure for reference file information.
 */
export class ReferenceFileInfoResponse {
  refFileList!: ReferenceFileInfo[];
}
/**
 * Metadata for a reference file.
 */
export class ReferenceFileInfo {
  fileName!: string;
  panelName!: string;
  lotNumber!: string;
  fileType!: string;
  revNumber!: string;
  assignmentValueVersion?: number | null;
  comment?: string;
  userName?: string;
  userId?: string;
  date?: string;
}

/**
 * One recorded change to a reference file or to the instrument register.
 *
 * Distinct from the study `AuditTrailEntry` despite the overlapping fields: this log
 * identifies what changed (`entityType` / `entityName`) rather than the app state a
 * study action was taken in (`isSuperUser` / `appVersion`).
 */
export interface ReferenceAuditEntry {
  name: string;
  userId?: string | null;
  date?: string | null;
  /** 'ReferenceFile' | 'InstrumentMapping' */
  entityType: string;
  entityName: string;
  /**
   * Snapshotted onto the log row when the change happened, so a Delete still describes
   * the file after the lot or spec row it came from is gone. Null on instrument rows;
   * `revNumber` is spec-only and `lotNumber` / `assignmentValueVersion` cal and control
   * only, matching the `ReferenceFileInfo` fields of the same name.
   */
  panelName?: string | null;
  fileType?: string | null;
  revNumber?: string | null;
  lotNumber?: string | null;
  assignmentValueVersion?: number | null;
  /** 'Upload' | 'Delete' | 'Upsert' */
  action: string;
  comment?: string | null;
}

export interface ReferenceAuditTrailResponse {
  auditTrail: ReferenceAuditEntry[];
}

/**
 * Sample information including accession and type details.
 */
export class Sample {
  accessionId!: string;
  sampleId!: string | null;
  defaultSampleType!: string;
  sampleType!: string;
  reqNumReplicates!: number;
  /**
   * Per-report-content-item sample type, keyed by reportContentItemId. Only present on a loaded
   * study; the Run Samples tab holds these as `RunSampleLot.runSamples` once applied.
   */
  sampleTypeOverrides?: Record<string, string> | null;
  //sampleResult?: SampleSummaryResult;
}

export type PhaseTypeOption = {
  value: string;
  label: string;
};

export const PhaseTypeOptions: PhaseTypeOption[] = [
  { value: 'RMQC', label: 'RMQC' },
  { value: 'PreBulk', label: 'PreBulk' },
  { value: 'ControlMatchKit', label: 'Control Match Kit' },
  { value: 'CalibratorMatchKit', label: 'Calibrator Match Kit' },
  { value: 'Stability', label: 'Stability' },
  { value: 'Control Assignment', label: 'Control Assignment' },
  { value: 'CalibratorAssignment', label: 'Calibrator Assignment' },
  { value: 'Calibrator Adjustment', label: 'Calibrator Adjustment' }
];
//  { 'RMQC', PhaseTypeId: PhaseTypeEnum.RMQC },
//  { PhaseTypeName: 'PreBulk', PhaseTypeId: PhaseTypeEnum.PreBulk },
//  { PhaseTypeName: 'Matched Kit', PhaseTypeId: PhaseTypeEnum.MatchedKit },
//  { PhaseTypeName: 'Stability', PhaseTypeId: PhaseTypeEnum.Stability },
//  { PhaseTypeName: 'Control Assignment', PhaseTypeId: PhaseTypeEnum.ControlAssignment },
//  { PhaseTypeName: 'Calibrator Assignment', PhaseTypeId: PhaseTypeEnum.CalibratorAssignment },
//  { PhaseTypeName: 'Calibrator Adjustment', PhaseTypeId: PhaseTypeEnum.CalibratorAdjustment },
//  { PhaseTypeName: 'Adjustment', PhaseTypeId: PhaseTypeEnum.Adjustment }
//];


///**
// * Enum representing different phase types for sample processing.
// */
//export enum PhaseTypeEnum {
//  RMQC = 1,
//  PreBulk = 2,
//  MatchedKit = 3,
//  Stability = 4,
//  ControlAssignment = 5,
//  CalibratorAssignment = 6,
//  CalibratorAdjustment = 7,
//  Adjustment = 8
//}
///**
// * Describes a phase type with its name and ID.
// */
//export class PhaseType {
//  PhaseTypeName!: string;
//  PhaseTypeId!: PhaseTypeEnum;
//}
///** All available phase types */
//export const PhaseTypes: PhaseType[] = [
//  { PhaseTypeName: 'RMQC', PhaseTypeId: PhaseTypeEnum.RMQC },
//  { PhaseTypeName: 'PreBulk', PhaseTypeId: PhaseTypeEnum.PreBulk },
//  { PhaseTypeName: 'Matched Kit', PhaseTypeId: PhaseTypeEnum.MatchedKit },
//  { PhaseTypeName: 'Stability', PhaseTypeId: PhaseTypeEnum.Stability },
//  { PhaseTypeName: 'Control Assignment', PhaseTypeId: PhaseTypeEnum.ControlAssignment },
//  { PhaseTypeName: 'Calibrator Assignment', PhaseTypeId: PhaseTypeEnum.CalibratorAssignment },
//  { PhaseTypeName: 'Calibrator Adjustment', PhaseTypeId: PhaseTypeEnum.CalibratorAdjustment },
//  { PhaseTypeName: 'Adjustment', PhaseTypeId: PhaseTypeEnum.Adjustment }
//];
///**
// * Returns the name of a phase type by its enum ID.
// * @param phaseType Phase type enum
// */
//export function getPhaseTypeById(phaseType: PhaseTypeEnum): string {
//  return PhaseTypes.find(x => x.PhaseTypeId === phaseType)?.PhaseTypeName || '';
//}
///**
// * Returns the name of a phase type by its enum ID.
// * @param phaseType Phase type enum
// */
//export function getPhaseTypeIdByName(phaseType: string): PhaseTypeEnum {
//  return PhaseTypes.find(x => x.PhaseTypeName === phaseType)?.PhaseTypeId || PhaseTypeEnum.RMQC;
//}

/**
 * Enum for assignment specification types.
 */
export enum AssignSpecTypeEnum {
  MiniBlend = 1,
  Formulation = 2,
  CalibratorAdjustment = 3
}
/**
 * Class representing a type of assignment specification.
 */
export class AssignSpecType {
  AssignSpecTypeName!: string;
  AssignSpecTypeId!: AssignSpecTypeEnum;
}
/** All assignment specification types */
export const AssignSpecTypes: AssignSpecType[] = [
  { AssignSpecTypeName: 'Mini Blend', AssignSpecTypeId: AssignSpecTypeEnum.MiniBlend },
  { AssignSpecTypeName: 'Formulation', AssignSpecTypeId: AssignSpecTypeEnum.Formulation },
  { AssignSpecTypeName: 'Calibrator Adjustment', AssignSpecTypeId: AssignSpecTypeEnum.CalibratorAdjustment }
];

/**
 * Get assignment specification name by enum ID.
 * @param assignSpecType Enum value
 */
export function getAssignSpecTypeById(assignSpecType: AssignSpecTypeEnum): string {
  return AssignSpecTypes.find(x => x.AssignSpecTypeId === assignSpecType)?.AssignSpecTypeName || '';
}

///**
// * Enum for different sample types.
// */
//export enum SampleTypeEnum {
//  Patient = 1,
//  CalCal = 2,
//  CalTest = 3,
//  ControlQC = 4,
//  ControlTest = 5,
//  Other = 6,
//  NA = 7
//}
///**
// * Represents a sample type with name and ID.
// */
//export class SampleType {
//  SampleTypeName!: string;
//  SampleTypeId!: SampleTypeEnum;
//}

///** List of all sample types */
//export const sampleTypes: SampleType[] = [
//  { SampleTypeName: 'Patient', SampleTypeId: SampleTypeEnum.Patient },
//  { SampleTypeName: 'Cal - Cal', SampleTypeId: SampleTypeEnum.CalCal },
//  { SampleTypeName: 'Cal - Test', SampleTypeId: SampleTypeEnum.CalTest },
//  { SampleTypeName: 'Control - QC', SampleTypeId: SampleTypeEnum.ControlQC },
//  { SampleTypeName: 'Control - Test', SampleTypeId: SampleTypeEnum.ControlTest },
//  { SampleTypeName: 'Other', SampleTypeId: SampleTypeEnum.Other },
//  { SampleTypeName: 'N/A', SampleTypeId: SampleTypeEnum.NA }
//];
/**
 * Enum to define how result values are generated.
 */
export enum ReportContentResultTypeEnum {
  Instrument = 1,
  Calculated = 2
}
/**
 * Describes a report result type.
 */
export class ReportContentResultType {
  resultTypeName!: string;
  resultTypeId!: ReportContentResultTypeEnum;
}
/** All possible result types for report content */
export const reportContentResultTypes: ReportContentResultType[] = [
  { resultTypeName: 'Instrument', resultTypeId: ReportContentResultTypeEnum.Instrument },
  { resultTypeName: 'Calculated', resultTypeId: ReportContentResultTypeEnum.Calculated }
];

/**
 * Represents a single row/item in the report content with dynamic support.
 */
export class ReportContentItem {
  constructor(columns: string[] = []) {
    this.reportContentItemId = this.lot + this.region + '-' + this.runId;

    columns.forEach((column) => {
      this[column] = ''; // Initialize dynamic columns to empty string
    });
  }

  reportContentItemId!: string;
  lot!: string;
  runId!: string;
  assay!: string;
  resultType!: string;
  region!: number;
  units!: string;
  resultDecimalPrecision!: number;
  userDefinedDecimalPrecision!: number;
  beadCouplingDate!: string;
  coupledBeadLn!: string;
  dyedBeadLn!: string;
  progenitorLn!: string;
  antigenLn!: string;
  antigenLoadLn!: string;
  coupledBeadPn!: string;
  ligandPn!: string;
  beadReagentPn!: string;
  sampleDiluentLn!: string;
  beadReagentLn!: string;
  conjugateReagentLn!: string;
  particleDiluentLn!: string;
  conjugateDiluentLn!: string;
  washBufferLn!: string;
  sheathFluidLn!: string;
  description!: string;
  calLot!: string;
  qcLot!: string;
  runValueOptimization?: string | boolean;
  [key: string]: string | number | boolean | undefined;
}
/**
 * Response object that includes dynamic report content and columns.
 */
export class ReportContentItemResponse {
  variableColumns: string[] = [];
  reportContentItems!: ReportContentItem[];
}

export class RunSampleLot {
  reportContentItemId!: number;
  meanType!: string;
  runSamples!: RunSample[];
}

export class RunSample {
  sampleId!: string;
  sampleType!: string;
}

/**
  * The order to display adjusted calibration lots according to the user-defined Run Report Items table. 
  */
export type CalAdjustedDisplayOrder = {
  kitLotOrder: string[];
  regionOrderByKitLot: Record<string, number[]>;
};

/** Supported mean types for calculations or UI toggles */
export const meanTypes = ['X', '-', 'x'];
